import type { PlacedComponent, Wire, CircuitResult, CircuitFault, WireColor } from './circuitTypes';
import { COMPONENT_DEFINITIONS } from './circuitTypes';

function getTerminalAbsPos(comp: PlacedComponent, terminalId: string) {
  const def = COMPONENT_DEFINITIONS[comp.type];
  const t = def.terminals.find((_, i) => `${comp.instanceId}-t${i}` === terminalId);
  if (!t) return null;
  return { x: comp.x + t.relX, y: comp.y + t.relY };
}

function getTerminalDef(comp: PlacedComponent, terminalId: string) {
  const def = COMPONENT_DEFINITIONS[comp.type];
  const idx = def.terminals.findIndex((_, i) => `${comp.instanceId}-t${i}` === terminalId);
  if (idx < 0) return null;
  return def.terminals[idx];
}

export function validateCircuit(
  components: PlacedComponent[],
  wires: Wire[]
): CircuitResult {
  const faults: CircuitFault[] = [];

  if (components.length === 0) {
    return {
      status: 'idle',
      faults: [],
      canSimulate: false,
    };
  }

  const hasSupply = components.some((c) => c.type === 'supply');
  const hasLamp = components.some((c) => c.type === 'lamp');
  const hasMcbOrRcbo = components.some((c) => c.type === 'mcb' || c.type === 'rcbo' || c.type === 'fuse');
  const hasSwitch = components.some((c) => c.type === 'switch-one-way' || c.type === 'switch-two-way');
  const hasSocket = components.some((c) => c.type === 'socket');

  if (wires.length === 0) {
    return {
      status: 'incomplete',
      faults: [
        {
          severity: 'info',
          code: 'NO_WIRES',
          title: 'No connections made',
          message: 'You have placed components but have not connected any wires yet. Click a terminal to start drawing a wire, then click another terminal to complete the connection.',
        },
      ],
      canSimulate: false,
    };
  }

  // Build adjacency: componentId -> set of connected componentIds with wire info
  const connectedTerminals = new Map<string, { compId: string; termId: string; color: WireColor }[]>();
  for (const w of wires) {
    if (!connectedTerminals.has(w.fromComponentId)) connectedTerminals.set(w.fromComponentId, []);
    if (!connectedTerminals.has(w.toComponentId)) connectedTerminals.set(w.toComponentId, []);
    connectedTerminals.get(w.fromComponentId)!.push({ compId: w.toComponentId, termId: w.toTerminalId, color: w.color });
    connectedTerminals.get(w.toComponentId)!.push({ compId: w.fromComponentId, termId: w.fromTerminalId, color: w.color });
  }

  // --- SHORT CIRCUIT DETECTION ---
  // Live directly connected to neutral on same or different components
  for (const w of wires) {
    const fromTerm = getTerminalDef(
      components.find((c) => c.instanceId === w.fromComponentId)!,
      w.fromTerminalId
    );
    const toTerm = getTerminalDef(
      components.find((c) => c.instanceId === w.toComponentId)!,
      w.toTerminalId
    );
    if (fromTerm && toTerm) {
      if (
        (fromTerm.color === 'live' && toTerm.color === 'neutral') ||
        (fromTerm.color === 'neutral' && toTerm.color === 'live')
      ) {
        faults.push({
          severity: 'error',
          code: 'SHORT_CIRCUIT',
          title: 'Short circuit detected',
          message: `A live terminal is directly connected to a neutral terminal (${w.fromTerminalId} → ${w.toTerminalId}). This creates a short circuit, which would immediately trip the MCB or blow a fuse — and could cause a fire or electric shock if unprotected. Remove this connection immediately.`,
        });
      }
      // Earth used as current-carrying conductor
      if (w.color === 'earth' && (fromTerm.color !== 'earth' || toTerm.color !== 'earth')) {
        faults.push({
          severity: 'error',
          code: 'EARTH_AS_CONDUCTOR',
          title: 'Earth used as current-carrying conductor',
          message: 'You have used a green/yellow earth wire to carry current between live or neutral terminals. The earth conductor is a protective conductor only — it must never carry normal operating current. This is a serious safety violation under BS 7671.',
        });
      }
    }
  }

  // Wrong wire colour used
  for (const w of wires) {
    const fromComp = components.find((c) => c.instanceId === w.fromComponentId);
    const toComp = components.find((c) => c.instanceId === w.toComponentId);
    if (!fromComp || !toComp) continue;
    const fromTerm = getTerminalDef(fromComp, w.fromTerminalId);
    const toTerm = getTerminalDef(toComp, w.toTerminalId);
    if (fromTerm && toTerm) {
      const expectedColor = fromTerm.color === toTerm.color ? fromTerm.color : null;
      if (expectedColor && w.color !== expectedColor) {
        faults.push({
          severity: 'warning',
          code: 'WRONG_WIRE_COLOUR',
          title: 'Incorrect wire colour',
          message: `The wire between ${fromComp.label} (${fromTerm.label}) and ${toComp.label} (${toTerm.label}) should be ${expectedColor === 'live' ? 'brown (live)' : expectedColor === 'neutral' ? 'blue (neutral)' : 'green/yellow (earth)'}. Using the wrong colour makes the installation non-compliant with BS 7671 and is dangerous for any person working on the installation later.`,
        });
      }
    }
  }

  // --- SWITCH INTERRUPTS NEUTRAL INSTEAD OF LIVE ---
  const switches = components.filter((c) => c.type === 'switch-one-way' || c.type === 'switch-two-way');
  for (const sw of switches) {
    const swWires = wires.filter((w) => w.fromComponentId === sw.instanceId || w.toComponentId === sw.instanceId);
    const hasNeutralOnSwitch = swWires.some((w) => {
      const fromComp = components.find((c) => c.instanceId === w.fromComponentId);
      const toComp = components.find((c) => c.instanceId === w.toComponentId);
      if (!fromComp || !toComp) return false;
      const fromTerm = getTerminalDef(fromComp, w.fromTerminalId);
      const toTerm = getTerminalDef(toComp, w.toTerminalId);
      return w.color === 'neutral' || fromTerm?.color === 'neutral' || toTerm?.color === 'neutral';
    });
    if (hasNeutralOnSwitch) {
      faults.push({
        severity: 'error',
        code: 'NEUTRAL_SWITCHED',
        title: 'Neutral conductor is being switched',
        message: `The switch "${sw.label}" appears to be interrupting the neutral conductor instead of the live. This means the lamp would appear off but the live conductor would still be connected to the lamp holder — creating a shock hazard when the lamp is changed. The switch must always be in the live (brown) conductor.`,
      });
    }
  }

  // --- LAMP CHECKS ---
  const lamps = components.filter((c) => c.type === 'lamp');
  for (const lamp of lamps) {
    const lampWires = wires.filter((w) => w.fromComponentId === lamp.instanceId || w.toComponentId === lamp.instanceId);
    const hasLive = lampWires.some((w) => w.color === 'live');
    const hasNeutral = lampWires.some((w) => w.color === 'neutral');
    const hasEarth = lampWires.some((w) => w.color === 'earth');

    if (hasLive && !hasNeutral) {
      faults.push({
        severity: 'error',
        code: 'MISSING_NEUTRAL_LAMP',
        title: `Missing neutral return on ${lamp.label}`,
        message: `The lamp "${lamp.label}" has a live connection but no neutral return path. Current cannot flow without a complete circuit. The lamp will not illuminate and the circuit is incomplete. Connect the neutral (blue) terminal of the lamp back to the neutral bar or supply neutral.`,
      });
    }
    if (!hasLive && hasNeutral) {
      faults.push({
        severity: 'warning',
        code: 'MISSING_LIVE_LAMP',
        title: `No live supply reaching ${lamp.label}`,
        message: `The lamp "${lamp.label}" has a neutral connection but no live conductor reaching it. Check that the switch is correctly wired in the live conductor and that the live path from the supply reaches the lamp.`,
      });
    }
    if (lampWires.length > 0 && !hasEarth) {
      faults.push({
        severity: 'warning',
        code: 'MISSING_EARTH_LAMP',
        title: `No earth connection on ${lamp.label}`,
        message: `The lamp "${lamp.label}" does not have an earth (CPC) connection. Class I luminaires require an earth connection for protection against electric shock in the event of a fault. Connect the earth (green/yellow) terminal to the earth bar.`,
      });
    }
  }

  // --- SUPPLY NOT CONNECTED ---
  const supply = components.find((c) => c.type === 'supply');
  if (supply) {
    const supplyWires = wires.filter((w) => w.fromComponentId === supply.instanceId || w.toComponentId === supply.instanceId);
    if (supplyWires.length === 0) {
      faults.push({
        severity: 'warning',
        code: 'SUPPLY_NOT_CONNECTED',
        title: 'Supply has no connections',
        message: 'The supply unit has been placed but none of its terminals (L, N, E) have been connected to the circuit. Start by connecting the supply live (L) terminal through a protective device (MCB/RCBO) to the circuit.',
      });
    }
  }

  // --- NO PROTECTIVE DEVICE ---
  if (hasSupply && (hasLamp || hasSocket) && !hasMcbOrRcbo) {
    faults.push({
      severity: 'warning',
      code: 'NO_PROTECTION',
      title: 'No overcurrent protection device',
      message: 'There is no MCB, RCBO, or fuse between the supply and the load. Every final circuit must be protected by an overcurrent protective device. Add an MCB or RCBO in the live conductor between the supply and the circuit.',
    });
  }

  // --- DETERMINE FINAL STATUS ---
  const hasErrors = faults.some((f) => f.severity === 'error');
  const hasShort = faults.some((f) => f.code === 'SHORT_CIRCUIT');
  const hasUnsafe = faults.some((f) => ['NEUTRAL_SWITCHED', 'EARTH_AS_CONDUCTOR'].includes(f.code));
  const hasWarnings = faults.some((f) => f.severity === 'warning');

  if (hasShort) {
    return { status: 'short-circuit', faults, canSimulate: false };
  }
  if (hasUnsafe) {
    return { status: 'unsafe', faults, canSimulate: false };
  }
  if (hasErrors) {
    return { status: 'incorrect', faults, canSimulate: false };
  }

  // Check for completeness — at minimum supply + load + wires
  if (!hasSupply) {
    faults.push({
      severity: 'info',
      code: 'NO_SUPPLY',
      title: 'No supply component placed',
      message: 'Add a Supply component to represent the incoming mains supply to the circuit.',
    });
    return { status: 'incomplete', faults, canSimulate: false };
  }

  if (!hasLamp && !hasSocket) {
    faults.push({
      severity: 'info',
      code: 'NO_LOAD',
      title: 'No load component placed',
      message: 'Add a lamp or socket outlet as the load for this circuit.',
    });
    return { status: 'incomplete', faults, canSimulate: false };
  }

  if (hasWarnings) {
    return { status: 'incorrect', faults, canSimulate: false };
  }

  // All checks passed
  faults.push({
    severity: 'info',
    code: 'ALL_PASS',
    title: 'Circuit validated successfully',
    message: 'All wiring checks passed. The circuit is correctly wired with the switch in the live conductor, correct wire colours, appropriate overcurrent protection, and an earth connection on all Class I equipment. Well done!',
  });

  return { status: 'correct', faults, canSimulate: true };
}